import {
  DeleteButton,
  EditButton,
  ListButton,
  RefreshButton,
  Show,
  useModal,
} from "@refinedev/antd";

import { Button, Space, Tag, Row, Col } from "antd";
import { IResourceComponentsProps, useNavigation, useShow } from "@refinedev/core";
import {
  FileProtectOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";

import dayjs from "dayjs";
import { IMetadataEnp } from "interfaces";
import React from "react";
import {
  ShowField,
  ShowSection,
  ShowFieldGrid,
  ShowModal,
} from "components/show";
import { formatEnrollmentDate } from "components/metadata/enrollmentDate";

export const MetadataEnpShow: React.FC<IResourceComponentsProps> = () => {
  const { result, query: { isLoading } } = useShow<IMetadataEnp>();
  const record = result;

  const { list } = useNavigation();

  const {
    modalProps: modalPropsSubscription,
    show: showSubscription,
    close: closeSubscription,
  } = useModal();

  return (
    <Show
      breadcrumb={false}
      headerProps={{
        onBack: () => window.history.back(),
        title: "Show ENP Service",
        subTitle: "active ENP service",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <Space>
            <ListButton resource="metadata-enp">ENP Service</ListButton>
            <EditButton resource="metadata-enp" recordItemId={record?.id} />
            <DeleteButton
              resource="metadata-enp"
              recordItemId={record?.id}
              onSuccess={() => list("metadata-enp")}
            />
            <RefreshButton resource="metadata-enp" recordItemId={record?.id} />
          </Space>
        ),
      }}
      isLoading={isLoading}
      footerButtons={
        <Space>
          <Button type="default" onClick={showSubscription}>
            Associated Subscription
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size={24} style={{ width: "100%" }}>
        {/* Service Overview */}
        <ShowSection
          title="e-NP Service"
          subtitle="Electronic Nautical Publications configuration"
          icon={<FileProtectOutlined />}
          variant="elevated"
        >
          <ShowFieldGrid columns={2}>
            <ShowField label="UKHO Licence" value={record?.subscription?.licence?.ukhoLicenceId} type="code" copyable />
            <ShowField label="Product Name" value={record?.productName} type="tag" tagColor="blue" />
            <ShowField label="Product Group" value={record?.productGroup} />
            <ShowField label="Licence Type" value={record?.licenceType} type="status" />
            <ShowField label="Activation Status" value={record?.activationStatus} type="status" />
            <ShowField label="Enrollment Date" value={formatEnrollmentDate(record?.enrollmentDate)} />
            <ShowField label="Expiry Date" value={record?.expiryDate} type="date" />
          </ShowFieldGrid>
        </ShowSection>

        {/* Subscription Details */}
        <ShowSection
          title="Subscription Details"
          subtitle="Billing and pricing information"
          variant="default"
        >
          <ShowFieldGrid columns={5}>
            <ShowField label="Currency" value={record?.currency} type="tag" tagColor="purple" />
            <ShowField label="Subscription Type" value={record?.subscriptionType} />
            <ShowField label="Payment Frequency" value={record?.paymentFrequency} />
            <ShowField label="Discount Percentage" value={record?.discountPercentage} type="percentage" />
            <ShowField label="Annual Flat Fee Per Vessel" value={record?.annualFlatFeePerVessel} type="currency" currency={record?.currency} />
          </ShowFieldGrid>
        </ShowSection>

        {/* Machine Configuration */}
        <ShowSection
          title="Machine Configuration"
          subtitle="Master and backup e-Reader setup"
          variant="subtle"
        >
          <Row gutter={24}>
            <Col xs={24} lg={12}>
              <ShowSection title="Master Configuration" variant="outlined" padding="compact">
                <ShowFieldGrid columns={1}>
                  <ShowField label="User Permit" value={record?.masterUserPermit} type="code" copyable />
                  <ShowField label="Machine ID" value={record?.masterMachineId} type="code" copyable />
                  <ShowField label="e-Reader Version" value={record?.masterEreaderVersion} />
                </ShowFieldGrid>
              </ShowSection>
            </Col>
            <Col xs={24} lg={12}>
              <ShowSection title="Backup Configuration" variant="outlined" padding="compact">
                <ShowFieldGrid columns={1}>
                  <ShowField label="User Permit" value={record?.backupUserPermit} type="code" copyable />
                  <ShowField label="Machine ID" value={record?.backupMachineId} type="code" copyable />
                  <ShowField label="e-Reader Version" value={record?.backupEreaderVersion} />
                </ShowFieldGrid>
              </ShowSection>
            </Col>
          </Row>
        </ShowSection>
      </Space>

      {/* Subscription Modal */}
      <ShowModal
        title="Associated Subscription"
        subtitle={record?.subscription?.subscriptionName}
        icon={<AppstoreOutlined />}
        open={modalPropsSubscription.open ?? false}
        onClose={closeSubscription}
        empty={!record?.subscription}
        emptyText="No subscription associated"
        width={700}
      >
        <ShowFieldGrid columns={2}>
          <ShowField label="UKHO Licence" value={record?.subscription?.licence?.ukhoLicenceId} type="code" copyable />
          <ShowField label="Subscription Name" value={record?.subscription?.subscriptionName} />
          <ShowField label="Service Type" value={record?.subscription?.serviceType} type="tag" tagColor="purple" />
          <ShowField
            label="Enrollment Date"
            value={record?.subscription?.enrollmentDate ? dayjs(record.subscription.enrollmentDate).format("DD-MMM-YYYY") : undefined}
          />
          <ShowField label="Licence Type" value={record?.subscription?.licenceType} type="status" />
          <ShowField label="Activation Status" value={record?.subscription?.activationStatus} type="status" />
          <ShowField label="Hold Status" value={record?.subscription?.holdStatus} type="status" />
          <ShowField label="Order Value Threshold" value={record?.subscription?.orderValueThreshold} type="currency" />
          <ShowField label="Automated Ordering" value={record?.subscription?.automatedOrdering} type="status" />
          <ShowField label="Automated Permit Issuance" value={record?.subscription?.automatedPermitIssuance} type="status" />
        </ShowFieldGrid>
      </ShowModal>
    </Show>
  );
};
